/** @format */

import { FormattedMessage } from 'gatsby-plugin-intl';
import React, { memo } from 'react';
import styled from 'styled-components';
import { GalleryIcon, VRButton, VrIcon } from './RoomGallery.style';

type FirstThreeType = (
  | {
      index: number;
      url: string;
      type: string;
      poster: any;
      alt?: undefined;
      thumbnail?: undefined;
      representative?: undefined;
    }
  | {
      index: number;
      url: string;
      alt: string;
      thumbnail: string;
      representative: boolean;
      type?: undefined;
      poster?: undefined;
    }
  | undefined
)[];

interface DesktopGalleryPreviewProps {
  firstThree: FirstThreeType;
  setShowInsideGallery: (show: boolean) => void;
  handleGTMPDPRoomGalleryImageAndAllClick: () => void;
  hasVR: boolean;
  handleClickVrIcon: () => void;
}

const DesktopGalleryPreview: React.FC<DesktopGalleryPreviewProps> = ({
  firstThree,
  setShowInsideGallery,
  handleGTMPDPRoomGalleryImageAndAllClick,
  hasVR,
  handleClickVrIcon,
}) => {
  return (
    <>
      {firstThree.map((photo, index) => {
        const firstPhotoURL = photo && index === 0 ? photo.url : photo?.thumbnail;
        return (
          <ImgContainer
            $length={firstThree.length}
            key={index}
            $rappresentative={Boolean(photo && photo.representative)}
            className="img-container lazy-load"
            onClick={() => {
              if (firstThree.length > 1) {
                setShowInsideGallery(true);
                handleGTMPDPRoomGalleryImageAndAllClick();
              }
            }}
          >
            <img
              key={index}
              src={photo?.representative ? photo.url : firstPhotoURL}
              alt={photo?.alt}
              data-src={photo?.representative ? photo.url : firstPhotoURL}
            />
            {photo?.representative && (
              <span>
                <FormattedMessage id="pdp.representative_image" />
              </span>
            )}
          </ImgContainer>
        );
      })}
      <GalleryButtonContainer>
        {hasVR && (
          <VRButton onClick={handleClickVrIcon} type="button" className="GalleryButtonContainer__vr-button">
            <img src={VrIcon} alt="Vr icon" />
            <div className="virtual-tour-content">Virtual tour</div>
          </VRButton>
        )}
        {firstThree.length > 1 && (
          <GalleryButton
            onClick={() => {
              handleGTMPDPRoomGalleryImageAndAllClick();
              setShowInsideGallery(true);
            }}
          >
            <img src={GalleryIcon} alt="Gallery icon" />
            <FormattedMessage id="pdp.show_all_photos" />
          </GalleryButton>
        )}
      </GalleryButtonContainer>
    </>
  );
};

export default memo(DesktopGalleryPreview);

const ImgContainer = styled.div<{ $rappresentative?: boolean; $length: number }>`
  cursor: pointer;
  height: 100%;
  width: 100%;
  border-radius: 1.875rem;
  overflow: hidden;
  position: relative;

  &:hover::after {
    ${props =>
      !props.$rappresentative &&
      `
      content: '';
      width: 100%;
      height: 100%;
      left: 0;
      position: absolute;
      z-index: 2;
      content: ' ';
      width: 100%;
      height: 100%;
      top: 0;
      background-color: rgba(0, 0, 0, 0.1);
      transition: background-color 350ms ease-in-out;
    `}
  }

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: ${({ theme }) => theme.font.title};
    font-size: clamp(1.125rem, 1.0417rem + 0.3704vw, 1.375rem);
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: clamp(0.0225rem, 0.0208rem + 0.0074vw, 0.0275rem);
    color: ${({ theme }) => theme.colors.joivy.white};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: ${({ $rappresentative }) => $rappresentative && 'blur(8px) brightness(90%)'};
  }

  &:nth-child(1) {
    grid-column: ${({ $length }) => ($length > 1 ? '1 / 6' : '1/9')};
    grid-row: ${({ $length }) => $length > 1 && '1 / 3'};
    border-top-right-radius: ${({ $length }) => $length > 1 && 0};
    border-bottom-right-radius: ${({ $length }) => $length > 1 && 0};
  }
  &:nth-child(2) {
    grid-column: 6 / 9;
    grid-row: ${({ $length }) => ($length > 2 ? '1 / 2' : '1/3')};
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  &:nth-child(3) {
    grid-column: 6 / 9;
    grid-row: 2 / 3;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    display: none;
  }
`;

const GalleryButtonContainer = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 22;

  & .GalleryButtonContainer__vr-button {
    @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
      display: none;
    }
  }
`;

const GalleryButton = styled.button`
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background-color: ${({ theme }) => theme.colors.joivy.pink};
  color: ${({ theme }) => theme.colors.joivy.white};
  border-radius: 5px;
  gap: 5px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.joivy['pink-light']};
  }

  span {
    font-family: ${({ theme }) => theme.font.body};
    font-weight: 500;
    font-size: 15px;
  }
  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    display: none;
  }
`;
